import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { Box } from "@zillow/constellation";
import Head from "next/head";
import { useRef, useEffect } from "react";
import { trackHubspotFormSubmit, trackHubspotEmbedView, trackHubspotEmbedHover, trackHubspotEmbedInteraction, trackHubspotFormFieldChange } from "./HubspotEmbed.clickstream.esm.js";
const HubspotEmbed = ({
  region,
  portalId,
  embedId,
  elementId,
  sfdcCampaignId
}) => {
  const hoverTimeoutRef = useRef({});
  const trackedHoversRef = useRef(/* @__PURE__ */ new Set());
  useEffect(() => {
    const currentHoverTimeouts = hoverTimeoutRef.current;
    const createContent = () => {
      if (window.hbspt) {
        window.hbspt.content.create({
          region,
          portalId,
          embedId,
          elementId,
          sfdcCampaignId
        });
        trackHubspotEmbedView(embedId, portalId);
        setTimeout(() => {
          setupEventTracking();
        }, 1e3);
      }
    };
    const setupEventTracking = () => {
      const embedContainer = document.getElementById(elementId);
      if (!embedContainer) return;
      const handleMouseEnter = (e) => {
        const target = e.target;
        const interactiveElement = target.closest('a, button, input, textarea, select, [role="button"]');
        if (interactiveElement) {
          const elementKey = getElementKey(interactiveElement);
          if (!trackedHoversRef.current.has(elementKey)) {
            hoverTimeoutRef.current[elementKey] = setTimeout(() => {
              const elementText = getElementText(interactiveElement);
              const elementType = getElementType(interactiveElement);
              trackHubspotEmbedHover(embedId, elementText, elementType);
              trackedHoversRef.current.add(elementKey);
            }, 500);
          }
        }
      };
      const handleMouseLeave = (e) => {
        const target = e.target;
        const interactiveElement = target.closest('a, button, input, textarea, select, [role="button"]');
        if (interactiveElement) {
          const elementKey = getElementKey(interactiveElement);
          if (hoverTimeoutRef.current[elementKey]) {
            clearTimeout(hoverTimeoutRef.current[elementKey]);
            delete hoverTimeoutRef.current[elementKey];
          }
        }
      };
      const handleClick = (e) => {
        const target = e.target;
        const clickableElement = target.closest('a, button, [role="button"]');
        if (clickableElement) {
          const elementText = getElementText(clickableElement);
          const elementUrl = clickableElement.tagName === "A" ? clickableElement.href : "";
          trackHubspotEmbedInteraction(embedId, "click", elementText, elementUrl);
        }
      };
      const handleFocus = (e) => {
        const target = e.target;
        if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT") {
          const inputElement = target;
          const fieldName = inputElement.name || inputElement.id || "unknown_field";
          const fieldLabel = getFieldLabel(inputElement);
          trackHubspotFormFieldChange(embedId, fieldName, fieldLabel);
        }
      };
      const handleChange = (e) => {
        const target = e.target;
        if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT") {
          const inputElement = target;
          const elementText = getElementText(inputElement);
          trackHubspotEmbedInteraction(embedId, "input", elementText);
        }
      };
      embedContainer.addEventListener("mouseenter", handleMouseEnter, true);
      embedContainer.addEventListener("mouseleave", handleMouseLeave, true);
      embedContainer.addEventListener("click", handleClick, true);
      embedContainer.addEventListener("focus", handleFocus, true);
      embedContainer.addEventListener("change", handleChange, true);
      return () => {
        embedContainer.removeEventListener("mouseenter", handleMouseEnter, true);
        embedContainer.removeEventListener("mouseleave", handleMouseLeave, true);
        embedContainer.removeEventListener("click", handleClick, true);
        embedContainer.removeEventListener("focus", handleFocus, true);
        embedContainer.removeEventListener("change", handleChange, true);
      };
    };
    const handleMessage = (event) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data.type === "hsFormCallback") {
          if (data.eventName === "onFormSubmitted") {
            trackHubspotFormSubmit(embedId, data.id || "unknown_form", true, true, data.data);
          } else if (data.eventName === "onFormSubmitFailed") {
            trackHubspotFormSubmit(embedId, data.id || "unknown_form", false, false);
          }
        }
      } catch (error) {
      }
    };
    window.addEventListener("message", handleMessage);
    if (window.hbspt) {
      createContent();
    } else {
      const script = document.getElementById("hs-embed");
      if (script) {
        const scriptWithReadyState = script;
        if (scriptWithReadyState.readyState === "complete" || scriptWithReadyState.readyState === "loaded") {
          createContent();
        } else {
          script.onload = createContent;
        }
      }
    }
    return () => {
      window.removeEventListener("message", handleMessage);
      Object.values(currentHoverTimeouts).forEach(clearTimeout);
    };
  }, [region, portalId, embedId, elementId, sfdcCampaignId]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Head, { children: /* @__PURE__ */ jsx("script", { id: "hs-embed", src: "https://js.hubspot.com/content-embed/v1.js", async: true }) }),
    /* @__PURE__ */ jsx(Box, { id: elementId, sx: {
      minHeight: "400px"
    }, "data-testid": "hubspot-embed-container" })
  ] });
};
function getElementKey(element) {
  const tagName = element.tagName.toLowerCase();
  const id = element.id;
  const className = element.className;
  const text = getElementText(element).substring(0, 20);
  return `${tagName}-${id}-${className}-${text}`;
}
function getElementText(element) {
  var _a;
  if (element.tagName === "INPUT" || element.tagName === "TEXTAREA" || element.tagName === "SELECT") {
    const inputElement = element;
    const label = getFieldLabel(inputElement);
    if (label) return label;
    if ("placeholder" in inputElement && inputElement.placeholder) {
      return inputElement.placeholder;
    }
    return inputElement.name || inputElement.id || "input_field";
  }
  const text = ((_a = element.textContent) == null ? void 0 : _a.trim()) || "";
  return text.substring(0, 100);
}
function getElementType(element) {
  const tagName = element.tagName.toLowerCase();
  if (tagName === "a") return "link";
  if (tagName === "button" || element.getAttribute("role") === "button") return "button";
  if (tagName === "input") {
    const inputType = element.type;
    return `input_${inputType}`;
  }
  if (tagName === "textarea") return "textarea";
  if (tagName === "select") return "select";
  return tagName;
}
function getFieldLabel(element) {
  var _a, _b;
  if (element.id) {
    const label = document.querySelector(`label[for="${element.id}"]`);
    if (label) {
      return ((_a = label.textContent) == null ? void 0 : _a.trim()) || "";
    }
  }
  const parentLabel = element.closest("label");
  if (parentLabel) {
    const clone = parentLabel.cloneNode(true);
    const inputs = clone.querySelectorAll("input, textarea, select");
    inputs.forEach((input) => input.remove());
    return ((_b = clone.textContent) == null ? void 0 : _b.trim()) || "";
  }
  const ariaLabel = element.getAttribute("aria-label");
  if (ariaLabel) {
    return ariaLabel;
  }
  if ("placeholder" in element && element.placeholder) {
    return element.placeholder;
  }
  return element.name || element.id || "unknown_field";
}
export {
  HubspotEmbed as default
};
